'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Ban, Loader2 } from 'lucide-react';
import { EditableInput, Wheel } from '@uiw/react-color';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormTextarea } from '@/components/forms/form-textarea';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { MatchThresholdSlider } from './match-threshold-slider';

const DEFAULT_WHEEL_COLOR = '#8b5cf6';

const DISPLAY_NAME_MAX = 60;
const DESC_MAX = 250;

/** What "add a field" starts from — module-level so the identity is stable. */
const EMPTY_FIELD_VALUES: CustomFieldFormValues = {
  display_name: '',
  display_name_en: '',
  description: '',
  color: null
};

export interface CustomFieldFormValues {
  display_name: string;
  /** Optional English label (OMA-594). Empty string means "not set". */
  display_name_en: string;
  description: string;
  color: string | null;
  /**
   * OMA-666 — the field's own duplicate match percentage; null inherits the
   * template's. Only set when the dialog is given `templateMatchThreshold`;
   * the supervisor's approval dialog never sees it.
   */
  match_threshold?: number | null;
}

/**
 * The one rule a field label has to satisfy: it must carry something to
 * slugify.
 *
 * `toFieldName()` (`template-config-page.tsx`) and its server-side twin
 * `FieldName::fromLabel()` (`backend/app/OcrTemplate/Support/FieldName.php`)
 * both strip everything outside `\p{L}\p{N}\s_`, so a punctuation-only label
 * cleans down to nothing and the frontend copy then falls back to
 * `field_${Date.now()}` — a NON-DETERMINISTIC extraction key, which is the one
 * outcome a label must not produce.
 */
export function labelHasAlphanumeric(label: string): boolean {
  return /[\p{L}\p{N}]/u.test(label);
}

/**
 * The dialog's schema, shared by create and edit mode — and, through the
 * exported component, by the supervisor's approval dialog. Exported so
 * `tests/unit/custom-field-label-validation.test.ts` can exercise it without
 * rendering.
 *
 * There is deliberately NO charset allowlist on either label. The label is
 * display-only, React-escaped text; the extraction key is derived from it
 * server-side by `FieldName::fromLabel()`, which strips everything outside
 * `\p{L}\p{N}\s_` anyway — `Voyage No.` yields `voyage_no` with or without the
 * dot — and neither `ApproveDiscoveredFieldRequest` (`label`) nor
 * `SyncCustomFieldsRequest`/`PromoteFieldsRequest` (`display_name`) puts a
 * charset rule on it. The allowlist that used to be here
 * (`/^[\p{L}\d\s_\-&/]+$/u`) rejected `.` `,` `:` `(` `)` `%` `#` and every
 * Arabic punctuation mark, so a discovered field whose label the model read off
 * the page — «Voyage No.» — could not be approved at all: Save never enabled.
 *
 * Do NOT re-derive this from `schemas.ts`: the regex there governs the template
 * NAME, which is a different field with a different rule.
 */
export function createCustomFieldSchema({
  t,
  existingNames
}: {
  t: (key: string) => string;
  existingNames: string[];
}) {
  return z.object({
    display_name: z
      .string()
      .min(2, t('validation.fieldNameMin'))
      .max(DISPLAY_NAME_MAX, t('validation.fieldNameMax'))
      .refine(labelHasAlphanumeric, t('validation.fieldNameNeedsAlnum'))
      .refine(
        (val) =>
          !existingNames.some(
            (n) => n.toLowerCase() === val.trim().toLowerCase()
          ),
        t('duplicateFieldName')
      ),
    // Optional by design: the ticket asked for a bilingual name, the user
    // scoped English down to nice-to-have. Only the length and "carries
    // something to slugify" rules apply, and only once something has been
    // typed.
    display_name_en: z
      .string()
      .max(DISPLAY_NAME_MAX, t('validation.fieldNameMax'))
      .refine(
        (val) => val.trim() === '' || labelHasAlphanumeric(val),
        t('validation.fieldNameNeedsAlnum')
      )
      .default(''),
    description: z
      .string()
      .max(DESC_MAX, t('validation.descriptionMax'))
      .default(''),
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .nullable()
  });
}

type CustomFieldSchema = ReturnType<typeof createCustomFieldSchema>;

interface AddCustomFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingNames: string[];
  onSave: (field: CustomFieldFormValues) => void;
  isPending?: boolean;
  mode?: 'create' | 'edit';
  initialValues?: CustomFieldFormValues;
  /**
   * The template's `undeclared_match_threshold`. Passing it shows the
   * per-field override (OMA-666); leaving it out hides it.
   */
  templateMatchThreshold?: number;
}

export function AddCustomFieldDialog({
  open,
  onOpenChange,
  existingNames,
  onSave,
  isPending,
  mode = 'create',
  initialValues,
  templateMatchThreshold
}: AddCustomFieldDialogProps) {
  const t = useTranslations('ocrTemplates');

  const isEdit = mode === 'edit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[460px]'>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('editField') : t('addField')}</DialogTitle>
        </DialogHeader>

        {/*
          The form lives in its own component INSIDE `DialogContent`, which
          Radix unmounts while the dialog is closed — so every open builds it
          fresh, seeded straight from `initialValues`.

          It must not be a `useForm` up here re-seeded by
          `useEffect(() => form.reset(initialValues), [open, initialValues])`,
          which is what this was. `reset()` clears react-hook-form's field
          registry, and that effect runs AFTER the fields have mounted, so the
          next render made every field register itself again — and a register
          of an unknown field reaches `_updateIsValidating`, which pushes onto
          the form-state subject synchronously, during render. React reported
          it as "Cannot update a component (`FormLabel`) while rendering a
          different component (`AddCustomFieldDialog`/`Controller`)". The
          supervisor's approve screen hit it three times per open, because
          `ApproveDiscoveredFieldDialog` builds `initialValues` as a fresh
          object literal each render and the effect's deps never settled.
        */}
        <CustomFieldForm
          existingNames={existingNames}
          onSave={onSave}
          onCancel={() => onOpenChange(false)}
          isPending={isPending}
          isEdit={isEdit}
          initialValues={initialValues}
          templateMatchThreshold={templateMatchThreshold}
        />
      </DialogContent>
    </Dialog>
  );
}

interface CustomFieldFormProps {
  existingNames: string[];
  onSave: (field: CustomFieldFormValues) => void;
  onCancel: () => void;
  isPending?: boolean;
  isEdit: boolean;
  initialValues?: CustomFieldFormValues;
  templateMatchThreshold?: number;
}

function CustomFieldForm({
  existingNames,
  onSave,
  onCancel,
  isPending,
  isEdit,
  initialValues,
  templateMatchThreshold
}: CustomFieldFormProps) {
  const t = useTranslations('ocrTemplates');

  // Kept outside the zod form: the value is a plain 0-100 the shared control
  // already clamps, and the schema is shared with the supervisor's approval
  // dialog, which has no such setting.
  const showMatchThreshold = templateMatchThreshold !== undefined;
  const initialMatchThreshold = initialValues?.match_threshold ?? null;
  const [overrideMatch, setOverrideMatch] = useState(
    initialMatchThreshold !== null
  );
  const [matchThreshold, setMatchThreshold] = useState(
    initialMatchThreshold ?? templateMatchThreshold ?? 100
  );

  const schema = createCustomFieldSchema({ t, existingNames });

  type FormValues = z.infer<CustomFieldSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    mode: 'onChange',
    defaultValues: initialValues ?? EMPTY_FIELD_VALUES
  });

  const displayNameValue = form.watch('display_name') ?? '';
  const displayNameEnValue = form.watch('display_name_en') ?? '';
  const colorValue = form.watch('color');

  function handleSubmit(values: FormValues) {
    onSave({
      display_name: values.display_name.trim(),
      display_name_en: values.display_name_en.trim(),
      description: values.description.trim(),
      color: values.color,
      ...(showMatchThreshold
        ? { match_threshold: overrideMatch ? matchThreshold : null }
        : {})
    });
  }

  return (
    <>
      <Form
        form={form}
        onSubmit={form.handleSubmit(handleSubmit)}
        className='min-h-0'
      >
        <div className='grid gap-4 py-4'>
          <div className='space-y-1'>
            <FormInput
              control={form.control}
              name='display_name'
              label={t('displayName')}
              placeholder={t('displayNamePlaceholder')}
              required
            />
            <p
              className={cn(
                'text-muted-foreground text-end text-xs',
                displayNameValue.length > DISPLAY_NAME_MAX && 'text-destructive'
              )}
            >
              {displayNameValue.length} / {DISPLAY_NAME_MAX}
            </p>
          </div>

          <div className='space-y-1'>
            <FormInput
              control={form.control}
              name='display_name_en'
              label={t('displayNameEn')}
              placeholder={t('displayNameEnPlaceholder')}
            />
            <p
              className={cn(
                'text-muted-foreground text-end text-xs',
                displayNameEnValue.length > DISPLAY_NAME_MAX &&
                  'text-destructive'
              )}
            >
              {displayNameEnValue.length} / {DISPLAY_NAME_MAX}
            </p>
            <p className='text-muted-foreground text-xs'>
              {t('displayNameEnHelper')}
            </p>
          </div>

          <FormTextarea
            control={form.control}
            name='description'
            label={t('fieldDescription')}
            placeholder={t('fieldDescriptionPlaceholder')}
            config={{ maxLength: DESC_MAX, rows: 3 }}
          />

          <div className='space-y-1'>
            <label className='text-sm font-medium'>{t('highlightColor')}</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className='w-full justify-start gap-2'
                >
                  {colorValue ? (
                    <span
                      className='h-4 w-4 shrink-0 rounded-full border'
                      style={{ backgroundColor: colorValue }}
                    />
                  ) : (
                    <Ban className='text-muted-foreground h-4 w-4 shrink-0' />
                  )}
                  {t('highlightColor')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto' align='start'>
                <div className='flex flex-col items-center gap-3'>
                  <Wheel
                    color={colorValue ?? DEFAULT_WHEEL_COLOR}
                    width={180}
                    height={180}
                    onChange={(next) =>
                      form.setValue('color', next.hex, {
                        shouldValidate: true,
                        shouldDirty: true
                      })
                    }
                  />
                  <div className='flex w-full items-center gap-2'>
                    <span
                      className='h-6 w-6 shrink-0 rounded-full border'
                      style={{
                        backgroundColor: colorValue ?? 'transparent'
                      }}
                    />
                    <span className='text-muted-foreground text-xs'>#</span>
                    <EditableInput
                      value={colorValue ? colorValue.slice(1) : ''}
                      placeholder='8B5CF6'
                      className='min-w-0 flex-1'
                      inputStyle={{ fontFamily: 'monospace' }}
                      onChange={(_evn, value) => {
                        if (typeof value !== 'string') return;
                        if (!/^[0-9A-Fa-f]{6}$/.test(value)) return;
                        form.setValue('color', `#${value.toUpperCase()}`, {
                          shouldValidate: true,
                          shouldDirty: true
                        });
                      }}
                    />
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='text-muted-foreground w-full justify-center gap-1.5'
                    onClick={() =>
                      form.setValue('color', null, {
                        shouldValidate: true,
                        shouldDirty: true
                      })
                    }
                  >
                    <Ban className='h-3.5 w-3.5' />
                    {t('noColor')}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {showMatchThreshold && (
            <div className='space-y-3 rounded-md border p-3'>
              <div className='flex items-start justify-between gap-3'>
                <Label
                  htmlFor='custom-field-threshold-override'
                  className='flex-col items-start gap-1'
                >
                  <span>{t('fieldMatchThresholdToggle')}</span>
                  <span className='text-muted-foreground text-xs font-normal'>
                    {t('fieldMatchThresholdToggleHint', {
                      value: templateMatchThreshold
                    })}
                  </span>
                </Label>
                <Switch
                  id='custom-field-threshold-override'
                  checked={overrideMatch}
                  onCheckedChange={setOverrideMatch}
                />
              </div>
              {overrideMatch && (
                <MatchThresholdSlider
                  id='custom-field-threshold'
                  label={t('fieldMatchThresholdLabel')}
                  ariaLabel={t('fieldMatchThresholdLabel')}
                  inputAriaLabel={t('fieldMatchThresholdValue')}
                  value={matchThreshold}
                  onValueChange={setMatchThreshold}
                  compact
                />
              )}
            </div>
          )}
        </div>
      </Form>

      <DialogFooter>
        <Button variant='outline' onClick={onCancel} disabled={isPending}>
          {t('cancel')}
        </Button>
        <Button
          onClick={form.handleSubmit(handleSubmit)}
          disabled={isPending || !form.formState.isValid}
        >
          {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
          {isEdit ? t('saveChanges') : t('saveField')}
        </Button>
      </DialogFooter>
    </>
  );
}
